import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActivityCategoryClient, ActivityCategoryDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new ActivityCategoryClient(API_BASE_URL, axiosInstance);

// Cached hook for all active activity categories (used in dropdowns/lookups)
export const useActivityCategories = () => {
  return useQuery({
    queryKey: ['activityCategories', 'all'],
    queryFn: async () => {
      const response = await apiClient.searchActivityCategories({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Non-cached search hook for admin pages with filters
export const useActivityCategorySearch = () => {
  const search = async (params: any) => {
    const response = await apiClient.searchActivityCategories(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get single activity category by ID
export const useActivityCategoryById = (id?: string) => {
  return useQuery({
    queryKey: ['activityCategories', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await apiClient.getActivityCategoryById(id);
      return response?.data || null;
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateActivityCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ActivityCategoryDTO) => {
      return await apiClient.createActivityCategory(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityCategories'] });
    },
  });
};

// Update mutation
export const useUpdateActivityCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ActivityCategoryDTO }) => {
      return await apiClient.updateActivityCategory(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityCategories'] });
    },
  });
};

// Delete mutation
export const useDeleteActivityCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteActivityCategory(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityCategories'] });
    },
  });
};